import { useEffect, type ReactElement } from 'react';
import { isRouteErrorResponse, useRouteError, useLocation } from 'react-router';
import { telemetryPage } from '@myrewards/contracts';
import { reportBrowserDiagnostic, reportBrowserFailure } from '../telemetry';
import { Hero, PageBody } from '../shell';
import { EmptyState } from '../ui';
import { noAccessFacts, type NoAccessFacts } from './access';

/** Any address the app doesn't have (yet). */
export function NotFoundPage(): ReactElement {
  const { pathname } = useLocation();
  useEffect(() => {
    reportBrowserDiagnostic({
      event: 'route_not_found',
      page: telemetryPage(pathname),
      diagnostic: 'not_found',
      status: 404,
    });
  }, [pathname]);
  return (
    <>
      <Hero variant="slim" title="Page not found" />
      <PageBody>
        <EmptyState
          variant="first-day"
          art="search"
          headingLevel={2}
          headline="This page isn’t here"
          body="It may have moved, or it isn’t built yet. Use Search & jump (Ctrl K) to find what you need, or go back to Home."
          action={{ label: 'Go to Home', href: '/' }}
        />
      </PageBody>
    </>
  );
}

/**
 * What No access says (APP-134 §5): that the person can't open the page, who it's for, and the way
 * back. APP-247: also for a page drawn inside another's hero (a Spot work tab) that knows from the
 * person's roles, before it reads anything, that they can't open it.
 */
export function NoAccessState({ facts }: { facts: NoAccessFacts }): ReactElement {
  return (
    <EmptyState
      variant="first-day"
      art="shield"
      headingLevel={2}
      headline="You don’t have access to this page"
      body={
        facts.detail ??
        (facts.for === null
          ? 'It’s for people with a role you don’t hold. If you need it, ask a System Admin.'
          : `It’s for ${facts.for}. If you need it, ask a System Admin.`)
      }
      action={{ label: 'Go to Home', href: '/' }}
    />
  );
}

/**
 * A page this person can't open (APP-134 §5): it says so, and who it's for. Records opened by id
 * never come here; one outside someone's scope is "not found", like one that doesn't exist.
 */
function NoAccess({ facts }: { facts: NoAccessFacts }): ReactElement {
  return (
    <>
      <Hero variant="slim" title="No access" />
      <PageBody>
        <NoAccessState facts={facts} />
      </PageBody>
    </>
  );
}

/** Shown inside the shell when a route fails to load or render. */
export function RouteError(): ReactElement {
  const error = useRouteError();
  const { pathname } = useLocation();
  useEffect(() => {
    reportBrowserFailure(error, 'route_error', pathname);
  }, [error, pathname]);
  const refused = noAccessFacts(error);
  if (refused !== null) return <NoAccess facts={refused} />;
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <>
      <Hero variant="slim" title={notFound ? 'Page not found' : 'Something went wrong'} />
      <PageBody>
        <EmptyState
          variant="first-day"
          art={notFound ? 'search' : 'shield'}
          headingLevel={2}
          headline={notFound ? 'This page isn’t here' : 'This page didn’t load'}
          body={
            notFound
              ? 'It may have moved. Use Search & jump (Ctrl K), or go back to Home.'
              : 'Nothing was changed. Reload the page, and if it happens again, contact the Stock Admin team.'
          }
          action={{ label: 'Go to Home', href: '/' }}
        />
      </PageBody>
    </>
  );
}
